import type { FC, HTMLAttributes } from "react";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import type { Placement } from "@react-types/overlays";
import { BookOpen01, ChevronSelectorVertical, LogOut01, User01 } from "@untitledui/icons";
import { useFocusManager } from "react-aria";
import type { DialogProps as AriaDialogProps } from "react-aria-components";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover } from "react-aria-components";
import { AvatarLabelGroup } from "@/components/base/avatar/avatar-label-group";
import { RadioButtonBase } from "@/components/base/radio-buttons/radio-buttons";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { cx } from "@/utils/cx";
import { useAccount } from "@/providers/account-provider";
import { SignOutConfirmModal } from "../sign-out-confirm-modal";
import { useMe } from "@/hooks/api/use-auth";
import { getUserCompanies } from "@/data/api/companies";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CompanyType } from "@/types/api/admin";
import { UserRole } from "@/types/api/auth";
import { switchCompany, getRedirectPath } from "@/hooks/api/use-auth";
import { setAuthToken, apiClient } from "@/lib/api-client";
import { storage } from "@/lib/local-storage";
import { AccountSwitchOverlay } from "../account-switch-overlay";
import { useTranslation } from "react-i18next";

type NavAccountType = {
    /** Unique identifier for the nav item. */
    id: string;
    /** Name of the account holder. */
    name: string;
    /** Email address of the account holder. */
    email: string;
    /** Avatar image URL. */
    avatar: string;
    /** Online status of the account holder. This is used to display the online status indicator. */
    status: "online" | "offline";
    /** User type for display */
    userType?: "SHIPPER" | "RECEIVER" | "ADMIN" | "FREIGHT_FORWARDER";
    /** Company ID */
    companyId?: string;
    /** Company name */
    companyName?: string;
    /** Company type */
    companyType?: string | null;
    /** User role */
    userRole?: UserRole;
};

// Helper function to get avatar URL - uses user avatar if available, otherwise EveMind logo
const getAvatarUrl = (userAvatarUrl: string | null | undefined): string => {
    // If user has uploaded an avatar, use it
    if (userAvatarUrl) {
        return userAvatarUrl;
    }
    // Otherwise, use EveMind logo as default
    return "/logo-eve.svg";
};

// Helper function to get user type from company type
const getUserTypeFromCompanyType = (companyType: string | null | undefined): "SHIPPER" | "RECEIVER" | "ADMIN" | "FREIGHT_FORWARDER" | undefined => {
    if (!companyType) return undefined;

    switch (companyType) {
        case CompanyType.SENDER:
            return "SHIPPER";
        case CompanyType.RECEIVER:
            return "RECEIVER";
        case CompanyType.FREIGHT_FORWARDER:
            return "FREIGHT_FORWARDER";
        default:
            return undefined;
    }
};

// Helper function to get badge label based on user role and company type
const getBadgeLabel = (
    userRole: UserRole | undefined,
    companyType: string | null | undefined,
    t: ReturnType<typeof useTranslation>['t']
): string => {
    // If user is admin, show "Admin" regardless of company type
    if (userRole === UserRole.SUPER_ADMIN || userRole === UserRole.COMPANY_ADMIN) {
        return t('types:teamRoles.admin');
    }

    // Otherwise, show translated company type
    if (!companyType) return '';

    // Map company type to translation key (convert snake_case to camelCase)
    const companyTypeMap: Record<string, string> = {
        'sender': 'sender',
        'receiver': 'receiver',
        'freight_forwarder': 'freightForwarder',
        'port_authority': 'portAuthority',
        'carrier': 'carrier',
        'customs_broker': 'customsBroker',
        'insurance_provider': 'insuranceProvider',
    };

    const companyTypeKey = companyTypeMap[companyType] || companyType;
    // Use the pages:companies namespace that's already loaded in useTranslation
    const translationKey = `list.filters.companyTypes.${companyTypeKey}`;
    const translated = t(translationKey, { ns: 'pages:companies' });

    // If translation not found, return formatted company type
    if (translated === translationKey) {
        return companyType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    return translated;
};

export const NavAccountMenu = ({
    className,
    selectedAccountId,
    accounts = [],
    onClose,
    onSignOutClick,
    onSwitchCompany,
    ...dialogProps
}: AriaDialogProps & {
    className?: string;
    accounts?: NavAccountType[];
    selectedAccountId?: string;
    onClose?: () => void;
    onSignOutClick?: () => void;
    onSwitchCompany?: (companyId: string, companyType: string | null) => void;
}) => {
    const { t } = useTranslation(['types', 'pages:companies', 'common']);
    const focusManager = useFocusManager();
    const dialogRef = useRef<HTMLDivElement>(null);

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    focusManager?.focusNext({ tabbable: true, wrap: true });
                    break;
                case "ArrowUp":
                    focusManager?.focusPrevious({ tabbable: true, wrap: true });
                    break;
            }
        },
        [focusManager],
    );

    useEffect(() => {
        const element = dialogRef.current;
        if (element) {
            element.addEventListener("keydown", onKeyDown);
        }

        return () => {
            if (element) {
                element.removeEventListener("keydown", onKeyDown);
            }
        };
    }, [onKeyDown]);


    return (
        <AriaDialog
            {...dialogProps}
            ref={dialogRef}
            className={cx("w-66 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden", className)}
        >
            <div className="rounded-xl bg-primary ring-1 ring-secondary">
                <div className="flex flex-col gap-0.5 py-1.5">
                    <NavAccountCardMenuItem
                        label={t('common:account.viewProfile', { defaultValue: "View profile" })}
                        icon={User01}
                        shortcut="⌘K->P"
                        onClick={(e) => {
                            e.stopPropagation();
                            // Determine profile URL based on current path
                            const location = window.location.pathname;
                            let profilePath = '/profile';
                            if (location.startsWith('/admin')) {
                                profilePath = '/admin/profile';
                            } else if (location.startsWith('/shipper')) {
                                profilePath = '/shipper/profile';
                            } else if (location.startsWith('/receiver')) {
                                profilePath = '/receiver/profile';
                            } else if (location.startsWith('/freight-forwarder')) {
                                profilePath = '/freight-forwarder/profile';
                            }
                            onClose?.();
                            window.location.href = profilePath;
                        }}
                    />
                    {accounts.some(a => a.userRole === UserRole.SUPER_ADMIN) && (
                        <NavAccountCardMenuItem
                            label={t('common:account.documentation', { defaultValue: "Documentation" })}
                            icon={BookOpen01}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose?.();
                                // Open internal documentation in new tab
                                window.open('https://docs.eve-mind.com', '_blank');
                            }}
                        />
                    )}
                </div>
                {accounts.length > 0 && (
                    <div className="flex flex-col gap-0.5 border-t border-secondary py-1.5">
                        <div className="px-3 pt-1.5 pb-1 text-xs font-semibold text-tertiary">{t('common:account.switchCompany', { defaultValue: "Switch company" })}</div>

                        <div className="flex flex-col gap-0.5 px-1.5">
                            {accounts.map((account) => (
                                <button
                                    key={account.id}
                                    onClick={() => {
                                        if (account.companyId && account.id !== selectedAccountId) {
                                            onSwitchCompany?.(account.companyId, account.companyType || null);
                                            onClose?.();
                                        }
                                    }}
                                    className={cx(
                                        "relative w-full cursor-pointer rounded-md px-2 py-1.5 text-left outline-focus-ring hover:bg-primary_hover focus:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
                                        account.id === selectedAccountId && "bg-primary_hover",
                                    )}
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div className="flex flex-col min-w-0 flex-1">
                                            {(account.companyType || account.userRole) && (
                                                <span className="inline-block w-fit max-w-xs text-xs text-tertiary px-1.5 py-0.5 rounded bg-secondary_alt mb-1">
                                                    {getBadgeLabel(account.userRole, account.companyType, t)}
                                                </span>
                                            )}
                                            <AvatarLabelGroup
                                                status="online"
                                                size="md"
                                                src={account.avatar}
                                                title={account.companyName || account.name}
                                                subtitle={account.email}
                                                className="min-w-0"
                                            />
                                        </div>
                                    </div>

                                    <RadioButtonBase isSelected={account.id === selectedAccountId} className="absolute top-2 right-2" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="pt-1 pb-1.5">
                <NavAccountCardMenuItem
                    label={t('common:account.signOut', { defaultValue: "Sign out" })}
                    icon={LogOut01}
                    shortcut="⌥⇧Q"
                    onClick={(e) => {
                        e.stopPropagation();
                        onSignOutClick?.();
                        onClose?.();
                    }}
                />
            </div>
        </AriaDialog>
    );
};

const NavAccountCardMenuItem = ({
    icon: Icon,
    label,
    shortcut,
    ...buttonProps
}: {
    icon?: FC<{ className?: string }>;
    label: string;
    shortcut?: string;
} & HTMLAttributes<HTMLButtonElement>) => {
    return (
        <button {...buttonProps} className={cx("group/item w-full cursor-pointer px-1.5 focus:outline-hidden", buttonProps.className)}>
            <div
                className={cx(
                    "flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
                    // Focus styles.
                    "outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
                )}
            >
                <div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
                    {Icon && <Icon className="size-5 text-fg-quaternary" />} {label}
                </div>

                {shortcut && (
                    <kbd className="flex rounded px-1 py-px font-body text-xs font-medium text-tertiary ring-1 ring-secondary ring-inset">{shortcut}</kbd>
                )}
            </div>
        </button>
    );
};

export const NavAccountCard = ({
    popoverPlacement,
    selectedAccountId: selectedAccountIdProp,
}: {
    popoverPlacement?: Placement;
    selectedAccountId?: string;
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const isDesktop = useBreakpoint("lg");
    const { signOut } = useAccount();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [showSignOutModal, setShowSignOutModal] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const { data: currentUser } = useMe();
    const queryClient = useQueryClient();

    // Get user companies
    const { data: userCompanies = [] } = useQuery({
        queryKey: ["user-companies"],
        queryFn: getUserCompanies,
        enabled: !!currentUser,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Convert user companies to NavAccountType
    const accounts: NavAccountType[] = useMemo(() => {
        if (!currentUser) return [];

        return userCompanies.map((company) => ({
            id: company.company_id,
            name: currentUser.full_name || currentUser.email,
            email: currentUser.email,
            avatar: getAvatarUrl(currentUser.avatar_url),
            status: "online" as const,
            userType: getUserTypeFromCompanyType(company.company_type),
            companyId: company.company_id,
            companyName: company.company_name,
            companyType: company.company_type,
            userRole: currentUser.role,
        }));
    }, [userCompanies, currentUser]);

    // Get current selected account (use currentUser.company_id to match active company)
    const selectedAccount = useMemo(() => {
        if (!currentUser) return null;

        // Use currentUser.company_id to find the active company (not primary company)
        const activeCompany = userCompanies.find(c => c.company_id === currentUser.company_id);

        // Fallback to primary company or first company if active company not found
        const fallbackCompany = userCompanies.find(c => c.is_primary_company) || userCompanies[0];
        const company = activeCompany || fallbackCompany;

        if (!company) return null;

        return {
            id: company.company_id,
            name: currentUser.full_name || currentUser.email,
            email: currentUser.email,
            avatar: getAvatarUrl(currentUser.avatar_url),
            status: "online" as const,
            userType: getUserTypeFromCompanyType(company.company_type),
            companyId: company.company_id,
            companyName: company.company_name,
            companyType: company.company_type,
            userRole: currentUser.role,
        };
    }, [userCompanies, currentUser]);

    const selectedAccountId = selectedAccountIdProp || selectedAccount?.id || "";

    const getRedirectPathForCompany = async (companyType: string | null, companyId?: string): Promise<string> => {
        if (!companyType) {
            return '/auth/login';
        }

        // Carica menu configuration se disponibile
        let menuConfig: Record<string, boolean> | undefined;
        if (companyId) {
            try {
                const configData = await apiClient<{ items: Record<string, boolean> }>(
                    `/menu-configurations/by-company/${companyId}`,
                    { requireAuth: true }
                );
                menuConfig = configData.items;
            } catch (error) {
                // Se il caricamento fallisce, usa comportamento di default
                console.warn('Failed to load menu configuration, using default', error);
            }
        }

        // Crea un oggetto UserInfo minimo per getRedirectPath
        const userInfo = {
            role: currentUser?.role || 'user',
            company_type: companyType as CompanyType,
            company_id: companyId,
        } as any;

        // Usa la funzione getRedirectPath con la menu configuration
        const redirectPath = getRedirectPath(userInfo, menuConfig);
        
        return redirectPath || '/auth/login';
    };

    const handleSwitchCompany = useCallback(async (companyId: string, companyType: string | null) => {
        // Don't switch if already on this company
        const currentCompanyId = currentUser?.company_id;
        if (currentCompanyId === companyId) {
            return;
        }

        setIsSwitching(true);

        try {
            // Call API to switch company and get new tokens
            const startTime = Date.now();
            const tokenResponse = await switchCompany(companyId);

            // Ensure modal is visible for at least 1 second
            const elapsed = Date.now() - startTime;
            const minDuration = 1000; // 1 second
            const remainingTime = Math.max(0, minDuration - elapsed);

            await new Promise(resolve => setTimeout(resolve, remainingTime));

            // Update tokens in localStorage
            setAuthToken(tokenResponse.access_token);
            storage.setItem("refresh_token", tokenResponse.refresh_token);

            // Update user info in query cache
            queryClient.setQueryData(["auth", "me"], tokenResponse.user);

            // Invalidate user companies query to refresh the list
            queryClient.invalidateQueries({ queryKey: ["user-companies"] });

            // Clear all other queries to ensure fresh data for new company
            queryClient.clear();

            // Redirect to the dashboard of the selected company con controllo menu configuration
            const redirectPath = await getRedirectPathForCompany(companyType, companyId);
            window.location.href = redirectPath;
        } catch (error: any) {
            console.error('Error switching company:', error);
            setIsSwitching(false);
            // Show error but don't redirect
            // The error will be handled by the UI
        }
    }, [currentUser, queryClient]);

    if (!selectedAccount || !currentUser) {
        return null;
    }

    return (
        <>
            <div ref={triggerRef} className="relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset">
                <AvatarLabelGroup
                    size="md"
                    src={selectedAccount.avatar}
                    title={selectedAccount.companyName || selectedAccount.name}
                    subtitle={selectedAccount.email}
                    status={selectedAccount.status}
                />

                <div className="absolute top-1.5 right-1.5">
                    <AriaDialogTrigger isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                        <AriaButton className="flex cursor-pointer items-center justify-center rounded-md p-1.5 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:bg-primary_hover pressed:text-fg-quaternary_hover">
                            <ChevronSelectorVertical className="size-4 shrink-0" />
                        </AriaButton>
                        <AriaPopover
                            aria-label="Account menu"
                            placement={popoverPlacement ?? (isDesktop ? "right bottom" : "top right")}
                            triggerRef={triggerRef}
                            offset={8}
                            className={({ isEntering, isExiting }) =>
                                cx(
                                    "origin-(--trigger-anchor-point) will-change-transform",
                                    isEntering &&
                                    "duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
                                    isExiting &&
                                    "duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
                                )
                            }
                        >
                            <NavAccountMenu
                                selectedAccountId={selectedAccountId}
                                accounts={accounts}
                                onClose={() => setIsPopoverOpen(false)}
                                onSignOutClick={() => setShowSignOutModal(true)}
                                onSwitchCompany={handleSwitchCompany}
                            />
                        </AriaPopover>
                    </AriaDialogTrigger>
                </div>
            </div>

            <SignOutConfirmModal
                isOpen={showSignOutModal}
                onClose={() => setShowSignOutModal(false)}
                onConfirm={() => {
                    signOut();
                    setShowSignOutModal(false);
                }}
            />

            <AccountSwitchOverlay isOpen={isSwitching} />
        </>
    );
};
